import React, { useCallback } from 'react';
import { Button, Input } from '@/components/ui';
import { CheckIcon } from './icons';
import {
    AUDIT_DATE_PRESETS,
    AUDIT_DATE_PRESET_ORDER,
    type AuditDatePreset,
    type AuditDatePresetLabels,
    type AuditDateRangeValue,
} from './auditDatePresets';

/**
 * EAA-1821: the single-select date menu body — presets plus the custom
 * from/to drawer — shown by BOTH the filter bar's "Thời gian" chip and the
 * table header's "Thời điểm" funnel.
 *
 * It stays in the app rather than in `packages/ui` (where the popover shell,
 * the multi-select list and the column trigger now live) because it is not
 * domain-free: "Hôm nay / 7 ngày / 90 ngày / Tuỳ chọn…" is this screen's own
 * vocabulary, and `resolveDateRange` in `auditDatePresets.ts` is what gives
 * those words meaning against the audit API. A Tier-1 version would have to
 * take the whole preset table as a prop, at which point the caller is writing
 * the component anyway.
 *
 * Both callers render THIS component — the chip briefly carried its own copy of
 * the markup mid-slice, which is how the header's date filter and the bar's
 * would have drifted apart while writing the same `AuditDateRangeValue`.
 */

export interface AuditDatePresetListProps {
    /** Accessible name of the radio group, e.g. "Thời điểm". */
    label: string;
    value: AuditDateRangeValue;
    onChange: (next: AuditDateRangeValue) => void;
    labels?: AuditDatePresetLabels;
}

export function AuditDatePresetList({
    label,
    value,
    onChange,
    labels,
}: AuditDatePresetListProps): React.ReactElement {
    const presetLabel = (preset: AuditDatePreset): string =>
        labels?.presets?.[preset] ?? AUDIT_DATE_PRESETS[preset];

    const handleSelectPreset = useCallback(
        (preset: AuditDatePreset) => {
            // EAA-1821: leaving `custom` drops the hand-typed bounds. Keeping them
            // would let a stale from/to ride along invisibly and reappear the next
            // time the operator picked "Tuỳ chọn…".
            if (preset === 'custom') {
                onChange({ preset, from: value.from, to: value.to });
                return;
            }
            onChange({ preset });
        },
        [onChange, value.from, value.to]
    );

    const handleFromChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ preset: 'custom', from: event.target.value, to: value.to });
        },
        [onChange, value.to]
    );

    const handleToChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange({ preset: 'custom', from: value.from, to: event.target.value });
        },
        [onChange, value.from]
    );

    return (
        <>
            <div className="portal-audit-filter-popover__list" role="radiogroup" aria-label={label}>
                {AUDIT_DATE_PRESET_ORDER.map((preset) => {
                    const checked = value.preset === preset;
                    return (
                        <Button
                            key={preset}
                            className={
                                checked
                                    ? 'portal-audit-filter-option is-checked'
                                    : 'portal-audit-filter-option'
                            }
                            role="radio"
                            aria-checked={checked}
                            onClick={() => handleSelectPreset(preset)}
                        >
                            {checked ? (
                                <CheckIcon className="portal-audit-filter-option__check" />
                            ) : (
                                <span
                                    className="portal-audit-filter-option__spacer"
                                    aria-hidden="true"
                                />
                            )}
                            <span className="portal-audit-filter-option__label">
                                {presetLabel(preset)}
                            </span>
                        </Button>
                    );
                })}
            </div>
            {value.preset === 'custom' && (
                <div className="portal-audit-filter-range">
                    <label className="portal-audit-filter-range__field">
                        {labels?.from ?? 'Từ'}
                        <Input
                            className="portal-audit-filter-range__input"
                            type="date"
                            value={value.from ?? ''}
                            onChange={handleFromChange}
                        />
                    </label>
                    <label className="portal-audit-filter-range__field">
                        {labels?.to ?? 'Đến'}
                        <Input
                            className="portal-audit-filter-range__input"
                            type="date"
                            value={value.to ?? ''}
                            onChange={handleToChange}
                        />
                    </label>
                </div>
            )}
        </>
    );
}
